// btc15/TabMap.js — "where is this on the tab, and what is it made of?"
//
// The user, 2026-09-16: the diagrams were abstractions of nothing. Two
// things fix that. TabMap draws the tab's ACTUAL layout, to its real
// proportions and in its real order, and rings the element being explained.
// RelationFlow draws the chain a number sits in, so it is clear that the
// price is not a fact on its own — it becomes a cost, then a contract count,
// then a payout.
//
// The layout below mirrors diamond/Btc15Panel.js top to bottom. If the tab
// is rearranged, rearrange this, or the map starts pointing at the wrong
// thing with total confidence.

const W = 150, H = 268;

// x, y, w, h in the miniature, plus the words that tell you what to look for.
export const SPOTS = {
  header:        { r: [8, 6, 134, 12], where: "the top bar — clock, 🎉, ⓘ and the Ⓩ/$ toggle" },
  "hero.target": { r: [8, 22, 64, 24], where: "the left half of the big pair at the top" },
  "hero.now":    { r: [78, 22, 64, 24], where: "the right half of the big pair, the one that ticks" },
  chart:         { r: [8, 50, 134, 44], where: "the chart, with the target ruled across it" },
  past:          { r: [8, 97, 134, 9], where: "the row of little arrows under the chart" },
  "market.up":   { r: [8, 110, 64, 34], where: "the green Up button" },
  "market.down": { r: [78, 110, 64, 34], where: "the red Down button" },
  "market.fee":  { r: [12, 122, 56, 8], where: "the small line inside each button: price + fee" },
  "market.allin":{ r: [12, 130, 56, 8], where: "the bold line under the fee: the all-in cost" },
  "market.mult": { r: [12, 137, 56, 6], where: "the “× · pays $1” line at the bottom of the button" },
  "market.mid":  { r: [8, 146, 134, 8], where: "the grey line under both buttons" },
  decided:       { r: [8, 158, 134, 14], where: "the thin progress bar labelled “Already decided”" },
  indicators:    { r: [8, 176, 134, 30], where: "the four small charts — Cost, Odds, Decided, Payoff" },
  "agents.grid": { r: [8, 210, 134, 30], where: "the grid of six agent cards" },
  "agents.prob": { r: [78, 213, 60, 9], where: "the big percentage on the right of an agent card" },
  "agents.judge":{ r: [12, 231, 126, 6], where: "the thin bar at the foot of each agent card" },
  rules:         { r: [8, 244, 134, 18], where: "the rules list at the bottom of the tab" },
  "sheet.agent": { r: [8, 210, 134, 30], where: "tap an agent card — STRATEGY · RECORD · CALIBRATION" },
  "sheet.window":{ r: [8, 50, 134, 44], where: "tap the chart or the target — RULES · ORDER BOOK · CHART" },
};

// The tab's skeleton, drawn once. Everything is a box in the real order.
const SKELETON = [
  ["header", "header"], ["hero.target", "TARGET"], ["hero.now", "NOW"],
  ["chart", "chart"], ["past", ""], ["market.up", "UP"], ["market.down", "DOWN"],
  ["market.mid", ""], ["decided", "already decided"], ["indicators", "indicators"],
  ["agents.grid", "agents"], ["rules", "rules"],
];

export default function TabMap({ spot, palette, caption }) {
  const P = palette || {};
  const hit = SPOTS[spot];
  return (
    <div data-testid={`btc15-tabmap-${spot || "none"}`} style={{ display: "flex", gap: 10, alignItems: "flex-start", margin: "2px 0 10px" }}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Where this sits on the tab: ${hit?.where || "the tab"}`}
        style={{ width: 96, minWidth: 96, height: "auto", background: P.surface, border: `1px solid ${P.border}`, borderRadius: 8 }}>
        <title>{`Where this sits on the tab: ${hit?.where || "the tab"}`}</title>
        {SKELETON.map(([key, label]) => {
          const [x, y, w, h] = SPOTS[key].r;
          return (
            <g key={key}>
              <rect x={x} y={y} width={w} height={h} rx="2.5" fill={P.border} opacity="0.35" />
              {label ? (
                <text x={x + 3} y={y + h / 2 + 2.6} fill={P.faint} fontSize="5.4" style={{ letterSpacing: 0.2 }}>{label}</text>
              ) : null}
            </g>
          );
        })}
        {hit && (
          <>
            <rect x={hit.r[0] - 2} y={hit.r[1] - 2} width={hit.r[2] + 4} height={hit.r[3] + 4} rx="3.5"
              fill={P.accent} opacity="0.22" />
            <rect x={hit.r[0] - 2} y={hit.r[1] - 2} width={hit.r[2] + 4} height={hit.r[3] + 4} rx="3.5"
              fill="none" stroke={P.accent} strokeWidth="1.6" />
          </>
        )}
      </svg>
      <div style={{ flex: 1, minWidth: 0, fontSize: 11, color: P.muted, lineHeight: 1.5, paddingTop: 2 }}>
        <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase", color: P.accent, display: "block", marginBottom: 3 }}>
          Find it on the tab
        </span>
        {hit?.where || "Shown throughout the tab."}
        {caption ? <span style={{ display: "block", marginTop: 4, color: P.faint }}>{caption}</span> : null}
      </div>
    </div>
  );
}

// ── relations ─────────────────────────────────────────────────────────────
// A number on this tab is rarely a fact by itself: the ask becomes a cost,
// the cost becomes a contract count, the count becomes a payout. These
// chains draw that, with the term you are reading lit up.
export const CHAINS = {
  cost: { label: "How a price becomes money", steps: [
    { id: "price", t: "ask" }, { id: "fee", t: "+ fee" }, { id: "allin", t: "all-in" },
    { id: "contracts", t: "÷ into stake" }, { id: "payout", t: "$1 each" },
  ] },
  belief: { label: "How a price becomes a decision", steps: [
    { id: "z", t: "z" }, { id: "fairvalue", t: "fair value" }, { id: "raw", t: "raw" },
    { id: "calibrated", t: "calibrated" }, { id: "agrees", t: "agrees?" }, { id: "decision", t: "wager" },
  ] },
  life: { label: "The life of a wager", steps: [
    { id: "decision", t: "entry" }, { id: "mark", t: "marked" }, { id: "grossnet", t: "fees in" }, { id: "trail", t: "exit rule" },
    { id: "closereason", t: "closed" }, { id: "pnl", t: "P&L" },
  ] },
  verdict: { label: "How a record becomes a verdict", steps: [
    { id: "pnl", t: "P&L" }, { id: "ror", t: "on risk" }, { id: "windows", t: "per window" },
    { id: "judgement", t: "200 windows" }, { id: "graduation", t: "ZBucks" },
  ] },
};

/** Which chain an entry belongs to, if any. */
export function chainFor(id) {
  for (const [key, chain] of Object.entries(CHAINS)) {
    if (chain.steps.some((s) => s.id === id)) return { key, ...chain };
  }
  return null;
}

export function RelationFlow({ id, palette, onPick }) {
  const P = palette || {};
  const chain = chainFor(id);
  if (!chain) return null;
  return (
    <div data-testid={`btc15-relations-${id}`} style={{ margin: "2px 0 10px" }}>
      <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase", color: P.muted, display: "block", marginBottom: 4 }}>
        {chain.label}
      </span>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 3 }}>
        {chain.steps.map((s, i) => (
          <span key={s.id} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
            {i > 0 && <span aria-hidden style={{ color: P.faint, fontSize: 10 }}>→</span>}
            <button
              type="button"
              data-testid={`btc15-relation-step-${s.id}`}
              onClick={() => onPick?.(s.id)}
              style={{
                fontSize: 10, fontWeight: s.id === id ? 900 : 600, padding: "3px 7px", borderRadius: 999,
                cursor: onPick ? "pointer" : "default", fontFamily: "inherit", whiteSpace: "nowrap",
                border: `1px solid ${s.id === id ? P.accent : P.border}`,
                background: s.id === id ? `${P.accent}22` : "transparent",
                color: s.id === id ? P.accent : P.muted,
              }}
            >
              {s.t}
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
